// Days as dates (docs/46, "Dates are dates"), as the kit's Days.swift does them; Days.swift is the reference.
//
// A to-do's day, a deadline, the end of a repeat and a project's day are calendar dates, not moments: a to-do for
// Monday is in Today once it is Monday where the browser is, whatever zone it was planned in.
//
// - Stored, a day is 12:00:00 UTC of its date. Noon UTC falls on the same date from UTC−11 to UTC+11, so iPhone 1.0,
//   which floors the stored moment in its own zone, reads it right too.
// - Read, a value of exactly 12:00:00 UTC is that UTC date. Any other value is an old one, local midnight of the
//   device that wrote it (iPhone 1.0 keeps writing them), and means the day whose local midnight is nearest to it in
//   the reading zone. A midnight is a whole quarter hour in UTC in every zone; a value that isn't (odd minutes, any
//   seconds) is a time of day that came along by mistake, and means the day it falls in, as it always did.
// - In memory, a day is a plain "yyyy-MM-dd" string (`Day`), never a JavaScript Date, so no zone can shift it. The
//   zone matters only when an old value is read, and for "today"; it is a parameter (the browser's zone at run time).

import type { Day, Weekday } from "./types";

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;
const NOON_MS = 43_200_000;
const QUARTER_HOUR_MS = 900_000;
const HOUR_MS = 3_600_000;

// MARK: - Days as text

/** Whether the text is a real calendar date, "yyyy-MM-dd" (not 30 February). */
export function isDay(text: unknown): text is Day {
  if (typeof text !== "string") return false;
  const match = DAY_PATTERN.exec(text);
  if (!match) return false;
  const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const utc = new Date(Date.UTC(year, month - 1, date));
  return utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === date;
}

/** The text as a day, or null when it isn't one. */
export function toDay(text: string): Day | null {
  return isDay(text) ? text : null;
}

/** A day from its year, month (1–12) and date; out-of-range parts roll over, as Date.UTC does. */
export function dayFrom(year: number, month: number, date: number): Day {
  return utcDayOf(new Date(Date.UTC(year, month - 1, date)));
}

/** The year, month (1–12) and date of a day. */
export function dayParts(day: Day): { year: number; month: number; date: number } {
  const match = DAY_PATTERN.exec(day);
  if (!match) throw new RangeError(`Not a day: ${day}`);
  return { year: Number(match[1]), month: Number(match[2]), date: Number(match[3]) };
}

/** The UTC date of a moment. Years outside 0–9999 don't occur in stored data. */
function utcDayOf(moment: Date): Day {
  const year = moment.getUTCFullYear();
  const month = moment.getUTCMonth() + 1;
  const date = moment.getUTCDate();
  return `${String(year).padStart(4, "0")}-${pad(month)}-${pad(date)}` as Day;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

// MARK: - Counting with days (Calendar's rules, in the Gregorian calendar)

export function addDays(day: Day, count: number): Day {
  const { year, month, date } = dayParts(day);
  return utcDayOf(new Date(Date.UTC(year, month - 1, date + count)));
}

/** Months later, on the same date or, when that month is shorter, its last day (31 Jan → 28 Feb), as Calendar does. */
export function addMonths(day: Day, count: number): Day {
  const { year, month, date } = dayParts(day);
  const first = new Date(Date.UTC(year, month - 1 + count, 1));
  const length = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return utcDayOf(new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(date, length))));
}

/** Years later; 29 February becomes 28 February in a year without it, as Calendar does. */
export function addYears(day: Day, count: number): Day {
  return addMonths(day, count * 12);
}

/** Whole days from one day to another; negative when `to` is earlier. */
export function daysBetween(from: Day, to: Day): number {
  const a = dayParts(from);
  const b = dayParts(to);
  return Math.round((Date.UTC(b.year, b.month - 1, b.date) - Date.UTC(a.year, a.month - 1, a.date)) / MS_PER_DAY);
}

/** 1 = Sunday … 7 = Saturday, as Foundation's Calendar counts. */
export function weekday(day: Day): Weekday {
  const { year, month, date } = dayParts(day);
  return (new Date(Date.UTC(year, month - 1, date)).getUTCDay() + 1) as Weekday;
}

/** The first day of the week the day is in. `firstWeekday` as in Calendar: 2 = Monday (Germany), 1 = Sunday. */
export function weekStart(day: Day, firstWeekday: Weekday = 2): Day {
  return addDays(day, -((weekday(day) - firstWeekday + 7) % 7));
}

/** Negative when `a` is earlier, positive when later, 0 for the same day. Days also compare as text. */
export function compareDays(a: Day, b: Day): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minDay(a: Day, b: Day): Day {
  return a <= b ? a : b;
}

export function maxDay(a: Day, b: Day): Day {
  return a >= b ? a : b;
}

// MARK: - Moments in a time zone

/** The zone the browser (or Node) runs in, by its IANA name: the zone days are read in and "today" is. */
export function currentZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string): Intl.DateTimeFormat {
  let format = formatters.get(zone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    formatters.set(zone, format);
  }
  return format;
}

interface LocalParts {
  year: number;
  month: number;
  date: number;
  hour: number;
  minute: number;
  second: number;
}

/** The wall-clock time of a moment in the zone (whole seconds). */
function localParts(ms: number, zone: string): LocalParts {
  const parts: Record<string, number> = {};
  for (const part of formatter(zone).formatToParts(new Date(ms))) {
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  }
  return {
    year: parts.year ?? 0, month: parts.month ?? 1, date: parts.day ?? 1,
    // Some engines say 24 for midnight even with h23.
    hour: (parts.hour ?? 0) % 24, minute: parts.minute ?? 0, second: parts.second ?? 0,
  };
}

/** How far the zone's clock is ahead of UTC at that moment, in milliseconds. */
function offsetAt(ms: number, zone: string): number {
  const p = localParts(ms, zone);
  const seconds = Math.floor(ms / 1000) * 1000;
  return Date.UTC(p.year, p.month - 1, p.date, p.hour, p.minute, p.second) - seconds;
}

/** The day a moment falls on in the zone (floored, as a calendar shows it). */
export function dayOf(moment: Date, zone: string): Day {
  const p = localParts(moment.getTime(), zone);
  return `${String(p.year).padStart(4, "0")}-${pad(p.month)}-${pad(p.date)}` as Day;
}

/** Today where the zone is. */
export function today(zone: string, now: Date = new Date()): Day {
  return dayOf(now, zone);
}

/** The time of a moment in the zone, as "HH:mm". */
export function timeOf(moment: Date, zone: string): string {
  const p = localParts(moment.getTime(), zone);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * Where the day begins in the zone, as `Calendar.startOfDay` finds it: its midnight, or on a day that begins later
 * (summer time starting at midnight, as in Chile), the first moment that has its date.
 */
export function startOfDay(day: Day, zone: string): Date {
  const { year, month, date } = dayParts(day);
  const guess = Date.UTC(year, month - 1, date);
  // The zone's offset, read back at the guess and again at the first answer, in case midnight is near a change.
  const first = guess - offsetAt(guess, zone);
  const second = guess - offsetAt(first, zone);
  for (const candidate of [Math.min(first, second), Math.max(first, second)]) {
    const p = localParts(candidate, zone);
    if (p.hour === 0 && p.minute === 0 && p.second === 0 && dayOf(new Date(candidate), zone) === day) return new Date(candidate);
  }
  // Midnight doesn't exist that day: the first second that has the date. Every zone is within UTC−12 and UTC+14, so
  // the day begins between 14 hours before its UTC midnight and 13 hours after it.
  let low = (guess - 15 * HOUR_MS) / 1000;
  let high = (guess + 13 * HOUR_MS) / 1000;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (dayOf(new Date(middle * 1000), zone) >= day) high = middle;
    else low = middle;
  }
  return new Date(high * 1000);
}

/** The moment of a time of day on a day, in the zone (for reminders). A time that doesn't exist moves on with the clock. */
export function at(day: Day, hour: number, minute: number, zone: string): Date {
  const { year, month, date } = dayParts(day);
  const guess = Date.UTC(year, month - 1, date, hour, minute);
  const first = guess - offsetAt(guess, zone);
  return new Date(guess - offsetAt(first, zone));
}

// MARK: - Stored days (DayStorage)

/** How a day is stored: 12:00:00 UTC of its date. */
export function storedDay(day: Day): Date {
  const { year, month, date } = dayParts(day);
  return new Date(Date.UTC(year, month - 1, date, 12));
}

/** Whether a stored value is written the new way: exactly 12:00:00.000 UTC. */
export function isNoonUTC(moment: Date): boolean {
  const ms = moment.getTime();
  return ((ms % MS_PER_DAY) + MS_PER_DAY) % MS_PER_DAY === NOON_MS;
}

/**
 * Whether a moment can be a midnight somewhere: every zone's offset is a whole quarter hour, so each zone's midnight
 * is one in UTC too (midnight in UTC+6 is 18:00 UTC, in Nepal's UTC+5:45 18:15 UTC).
 */
export function isQuarterHour(moment: Date): boolean {
  const ms = moment.getTime();
  return ((ms % QUARTER_HOUR_MS) + QUARTER_HOUR_MS) % QUARTER_HOUR_MS === 0;
}

/**
 * The day a stored value means, read in `zone`: its UTC date when it is noon UTC; otherwise the day whose midnight in
 * the zone is nearest to it (exactly between two, the earlier, as floored); a value that can't be a midnight anywhere
 * is the day it falls in. `wholeMilliseconds` is false for a stored value finer than a millisecond (a Timestamp's
 * nanoseconds), which a Date can't hold: such a value is a time of day too.
 */
export function dayOfStored(stored: Date, zone: string, wholeMilliseconds = true): Day {
  if (!wholeMilliseconds) return dayOf(stored, zone);
  if (isNoonUTC(stored)) return utcDayOf(stored);
  const floor = dayOf(stored, zone);
  if (!isQuarterHour(stored)) return floor;
  const next = addDays(floor, 1);
  const ms = stored.getTime();
  const sinceStart = ms - startOfDay(floor, zone).getTime();
  const untilNext = startOfDay(next, zone).getTime() - ms;
  return sinceStart <= untilNext ? floor : next;
}

/** The value a stored one becomes in the new form (the account's one-time conversion): unchanged when it is one already. */
export function convertedStored(stored: Date, zone: string): Date {
  return isNoonUTC(stored) ? stored : storedDay(dayOfStored(stored, zone));
}
